/**
 * 权限指令
 * 用于控制DOM元素的显示/隐藏基于用户权限
 */
import tokenService from '@/utils/tokenService';
import permissionManager from '@/utils/permissionManager';
import { USER_ROLE_ADMIN, hasDevToolsAccess } from '@/constants/userRole';

/**
 * 检查权限
 * @param {string} permission - 权限类型 ('admin', 'login', 'guest')
 * @returns {boolean} 是否有权限
 */
function checkPermission(permission) {
  // 使用权限管理器进行检查，确保状态一致性
  return permissionManager.checkPermission(permission);
}

/**
 * 应用权限检查结果到元素
 */
function applyPermissionToElement(el, permission, modifiers) {
  const hasPermission = checkPermission(permission);

  if (!hasPermission) {
    // 没有权限时的处理
    if (modifiers.remove) {
      // 从DOM中移除元素
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    } else {
      // 隐藏元素
      el.style.display = 'none';
    }
  } else {
    // 有权限时显示元素
    if (!modifiers.remove) {
      el.style.display = '';
    }
  }
}

/**
 * 权限指令定义
 */
export const permission = {
  // Vue 3 的指令钩子
  mounted(el, binding) {
    const { value, modifiers } = binding;

    // 初始权限检查
    applyPermissionToElement(el, value, modifiers);

    // 添加权限变更监听器
    const removeListener = permissionManager.addPermissionListener(() => {
      // 权限状态变化时重新检查
      applyPermissionToElement(el, value, modifiers);
    });

    // 将移除监听器的函数保存到元素上，用于unmounted时清理
    el._permissionCleanup = removeListener;
  },

  // 当绑定值更新时重新检查权限
  updated(el, binding) {
    const { value, modifiers } = binding;
    applyPermissionToElement(el, value, modifiers);
  },

  // 组件卸载时清理监听器
  unmounted(el) {
    if (el._permissionCleanup) {
      el._permissionCleanup();
      delete el._permissionCleanup;
    }
  }
};

/**
 * 权限检查函数，可在组件中直接使用
 * @param {string} permission - 权限类型
 * @returns {boolean} 是否有权限
 */
export function hasPermission(permission) {
  return checkPermission(permission);
}

/**
 * 获取当前用户权限信息
 * @returns {Object|null} 用户权限信息
 */
export function getCurrentPermissions() {
  const state = permissionManager.getCurrentPermissionState();
  const userInfo = state.userInfo;

  if (!state.isLoggedIn || !userInfo) {
    return {
      isGuest: true,
      isLoggedIn: false,
      isAdmin: false,
      hasDevTools: false,
      userRole: 0
    };
  }

  return {
    isGuest: false,
    isLoggedIn: true,
    isAdmin: userInfo.userRole === USER_ROLE_ADMIN,
    hasDevTools: hasDevToolsAccess(userInfo.userRole),
    userRole: userInfo.userRole,
    userId: userInfo.userId,
    username: userInfo.username
  };
}

/**
 * 响应式权限检查 Hook
 * @param {string} permission - 权限类型
 * @returns {ComputedRef<boolean>} 响应式权限状态
 */
export function usePermission(permission) {
  return permissionManager.usePermission(permission);
}

/**
 * 响应式用户信息 Hook
 * @returns {ComputedRef<Object|null>} 响应式用户信息
 */
export function useUserInfo() {
  return permissionManager.useUserInfo();
}

/**
 * 响应式登录状态 Hook
 * @returns {ComputedRef<boolean>} 响应式登录状态
 */
export function useLoginState() {
  return permissionManager.useLoginState();
}

export default permission;
